<script setup>
import { computed } from 'vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION } from '../constants';
import {
  isRevokedMessage,
  lifecycleFilletTone,
} from 'dashboard/helper/messageLifecycle';
import MessageLifecycle from './MessageLifecycle.vue';

// SiteUp V2 (F1.7): a linha da hora NÃO mora mais aqui dentro. Sobre a bolha
// colorida (azul do agente, iris do bot) o cinza neutro do texto sumia, então
// o MessageMeta passou a ser renderizado pelo Message.vue, na área
// [grid-area:meta], logo abaixo da bolha e sobre o fundo do app.
const { variant, orientation, inReplyTo, contentAttributes, createdAt } =
  useMessageContext();
const { t } = useI18n();

const varaintBaseMap = {
  // O `!` não é preguiça: o link renderizado no corpo da mensagem sai com a
  // classe `.link`, cuja regra global tem especificidade maior que a do
  // `[&_a]` do bubble. Sem o important o link ficava rgb(39,129,246) sobre
  // bolha rgb(29,155,240) — medido no staging, praticamente invisível.
  [MESSAGE_VARIANTS.AGENT]:
    'bg-n-blue-9 text-white [&_a]:!text-white [&_a]:underline',
  [MESSAGE_VARIANTS.PRIVATE]:
    'bg-transparent border border-n-amber-6 ltr:border-l-[3px] rtl:border-r-[3px] ltr:border-l-n-amber-9 rtl:border-r-n-amber-9 text-n-amber-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]: 'bg-n-slate-4 text-n-slate-12',
  [MESSAGE_VARIANTS.ACTIVITY]: 'text-n-slate-11 text-xs',
  [MESSAGE_VARIANTS.BOT]:
    'bg-n-slate-4 ltr:border-l-[3px] rtl:border-r-[3px] ltr:border-l-n-iris-9 rtl:border-r-n-iris-9 text-n-slate-12',
  [MESSAGE_VARIANTS.TEMPLATE]:
    'bg-n-slate-4 border border-n-weak text-n-slate-12',
  [MESSAGE_VARIANTS.ERROR]:
    'bg-transparent border border-n-ruby-6 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-slate-3 border border-dashed border-n-strong text-n-slate-11',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-[20px] ltr:rounded-bl-[5px] rtl:rounded-br-[5px]',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-[20px] ltr:rounded-br-[5px] rtl:rounded-bl-[5px]',
  [ORIENTATION.CENTER]: 'rounded-md',
};

// M3/M4 — filete na borda esquerda: vermelho apagada, âmbar editada.
// `!` no token (mesmo padrão do link da bolha) vence o filete iris do bot
// e o âmbar da nota privada.
const filletClassByTone = {
  revoked:
    'ltr:border-l-[3px] rtl:border-r-[3px] ltr:!border-l-n-ruby-9 rtl:!border-r-n-ruby-9',
  edited:
    'ltr:border-l-[3px] rtl:border-r-[3px] ltr:!border-l-n-amber-9 rtl:!border-r-n-amber-9',
};

const attrs = computed(() => contentAttributes?.value || {});
const filletTone = computed(() => lifecycleFilletTone(attrs.value));
const isRevoked = computed(() => isRevokedMessage(attrs.value));
const isEdited = computed(() => Boolean(attrs.value.edited));

const messageClass = computed(() => {
  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-xl');
  }

  if (filletTone.value) {
    classToApply.push(filletClassByTone[filletTone.value]);
  }

  return classToApply;
});

const scrollToMessage = () => {
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};

const replyToPreview = computed(() => {
  if (!inReplyTo.value) return '';

  const { content, attachments } = inReplyTo.value;

  if (content) return new MessageFormatter(content).formattedMessage;
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});
</script>

<template>
  <div
    class="text-sm min-w-0"
    :class="[
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL,
      },
    ]"
    :data-lifecycle="filletTone || undefined"
  >
    <div
      v-if="inReplyTo"
      class="p-2 -mx-1 mb-2 rounded-2xl cursor-pointer bg-n-alpha-black1 ltr:border-l-2 rtl:border-r-2 border-current/40 line-clamp-2"
      @click="scrollToMessage"
    >
      <div
        v-dompurify-html="replyToPreview"
        class="prose prose-bubble line-clamp-2"
      />
    </div>
    <!-- M3: texto PRESERVADO — só baixa a ênfase (opacidade herda a cor da bolha). -->
    <div :class="{ 'opacity-70': isRevoked }" data-testid="message-bubble-body">
      <slot />
    </div>
    <MessageLifecycle
      :revoked-at="attrs.revokedAt || ''"
      :revoked-by="attrs.revokedBy || ''"
      :edited="isEdited"
      :edited-at="attrs.editedAt || ''"
      :original-content="attrs.originalContent || ''"
      :created-at="createdAt"
    />
  </div>
</template>
